/**
 * viewer2 standalone entry point. Mounts the viewer fullscreen and reads
 * the scene from `?scene=<name>` (or `?sceneUrl=<path>`).
 *
 * `?demo=1` is the stand demo (opened from demo.html, the Home Screen app):
 * registers the offline-cache worker and mounts in demo mode; `?fps=1`
 * adds the frame-rate readout.
 *
 * For embedding into a host page, import { mount } from './mount' directly.
 */

import { mount, type ViewerHandle } from './mount';

function pickSceneUrl(): string {
    const url = new URL(window.location.href);
    const explicit = url.searchParams.get('sceneUrl');
    if (explicit) return explicit;
    const name = url.searchParams.get('scene') ?? 'test';
    // BASE_URL ('/' normally) keeps static scenes working when the viewer is
    // deployed under a subpath (vite build --base=/viewer/ — used to co-host
    // a preview viewer inside the site preview worker).
    return `${import.meta.env.BASE_URL}scenes/${name}/scene.json`;
}

async function main(): Promise<void> {
    const root = document.getElementById('viewer-root');
    if (!root) {
        throw new Error('viewer2: #viewer-root not found in DOM');
    }
    // Standalone mode: index.html ships #viewer-root empty (the static boot
    // loader lives at body level, so it survives this) and mount() builds the
    // canvas + ui inside it. Clear defensively in case of stale markup.
    root.innerHTML = '';

    const params = new URL(window.location.href).searchParams;
    const demo = params.get('demo') === '1';
    const fps = params.get('fps') === '1';
    if (demo && 'serviceWorker' in navigator) {
        navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => { /* http dev, or refused */ });
    }

    const handle: ViewerHandle = await mount({
        container: root,
        sceneUrl: pickSceneUrl(),
        demo,
        ui: fps ? { fps: true } : undefined
    });

    // Expose for ad-hoc debugging in devtools.
    const w = window as unknown as Record<string, unknown>;
    w.__viewer = handle.app;
    w.__scene = handle.scene;
    w.__quality = handle.quality;
    w.__handle = handle;
}

main().catch((err) => {
    console.error('viewer2: failed to start', err);
    // If we died before mount() took ownership of the loading UI (e.g. WebGL
    // unavailable), don't leave the static boot loader animating forever.
    document.getElementById('ollo-boot-loading')?.remove();
});
